import { useEffect, useState } from "react";

import type { TransactionState } from "../../types/transaction";

/**
 * Phase variable used by the in-place trade-widget flows (LandingBuyFlow,
 * BuyFlow, SellFlow, StakeFlow, UnstakeFlow, WithdrawFlow). Each form
 * collapses inputs / expands a receipt / transitions the CTA based on the
 * current phase.
 *
 * - `"empty"` — no input or input cleared. Default chrome.
 * - `"focused"` — landing-style focus (only used by LandingBuyFlow).
 * - `"filled"` — user has entered a value but hasn't submitted.
 * - `"pending"` — submit kicked off, awaiting signature / confirmation.
 * - `"processing"` — the on-chain submission is in flight after signing.
 * - `"success"` — transaction confirmed.
 * - `"postSuccess"` — buy flow only — celebration window before reverting.
 * - `"error"` — submit failed; card stays mounted with an inline error.
 */
export type TransactionPhase =
    | "empty"
    | "focused"
    | "filled"
    | "pending"
    | "processing"
    | "success"
    | "postSuccess"
    | "error";

interface UseTransactionPhaseOptions {
    /** The form's tx state (from useTransactionForm or useSwap). */
    isPending: boolean;
    isSuccess: boolean;
    /** Underlying transaction-state machine — drives processing vs signing
     *  distinction within `pending`. */
    executeState: TransactionState;
    /** Whether the user has typed a non-empty / non-zero value. */
    hasValue: boolean;
    /** Optional override — when true and isPending is false, force the
     *  phase to `"focused"` regardless of value. Used by the landing flow. */
    isFocused?: boolean;
    /** When provided (>0), transition `success → postSuccess` this many ms
     *  after entering success. Mirrors the showcase BuyFlow celebration
     *  window before the EarnsCard reveal. Buy mode passes 1500; everyone
     *  else leaves it undefined so phase plateaus at success. */
    postSuccessDelay?: number;
}

/**
 * Derive the in-place flow phase from the form's submit state + input state.
 *
 * Phase progression on a happy path: `empty → filled → pending → processing
 * → success` (and `postSuccess` for buy flows that show a celebration before
 * the next CTA).
 *
 * `postSuccessDelay` opts in to the success → postSuccess transition via an
 * internal timer. Without it the phase plateaus at success (no celebration).
 */
export function useTransactionPhase({
    isPending,
    isSuccess,
    executeState,
    hasValue,
    isFocused = false,
    postSuccessDelay
}: UseTransactionPhaseOptions): TransactionPhase {
    const [isPostSuccess, setIsPostSuccess] = useState(false);

    useEffect(() => {
        if (!isSuccess) {
            if (isPostSuccess) setIsPostSuccess(false);
            return;
        }
        if (postSuccessDelay === undefined || postSuccessDelay <= 0) return;
        if (isPostSuccess) return;
        const t = setTimeout(() => setIsPostSuccess(true), postSuccessDelay);
        return (): void => clearTimeout(t);
    }, [isSuccess, postSuccessDelay, isPostSuccess]);

    if (executeState.isError) return "error";
    if (isSuccess && isPostSuccess) return "postSuccess";
    if (isSuccess) return "success";
    if (isPending) {
        // Distinguish "signing wait" (pending) from "on-chain submission
        // in flight" (processing) so the receipt block can transition.
        return executeState.step === "confirming" || executeState.step === "complete" ? "processing" : "pending";
    }
    if (hasValue) return "filled";
    if (isFocused) return "focused";
    return "empty";
}
